<template>
  <form-layout
    class="crew-form"
    entity-name="crew"
    :loading="isLoading"
    @save="saveCrew"
  >
    <template #title>
      <h1 class="text-lg uppercase font-bold">{{ titleText }}</h1>
    </template>

    <template #body>
      <bdg-text-field
        ref="identifier"
        name="Crew identifier"
        v-model="crewData.identifier"
        required
      />

      <bdg-dropdown
        ref="type"
        name="Type"
        :options="crewTypes"
        placeholder="Select one"
        v-model="crewData.type"
        required
      />

      <bdg-dropdown
        ref="supervisor"
        name="Crew supervisor"
        :options="allCrewSupervisors"
        placeholder="Select one"
        v-model="crewData.crew_supervisor_id"
        searchable
        required
      />

      <bdg-dropdown
        ref="leader"
        name="Crew leader"
        :options="allCrewLeaders"
        placeholder="Select one"
        v-model="crewData.crew_leader_id"
        searchable
        required
      />

      <bdg-multi-select
        name="Crew laborers"
        empty-state-message="No laborers added"
        begin-typing-message="Begin typing laborer name here"
        :list-item-component="freeResponseCellComponent"
        :add-new-context-item="addNewContextItem"
        :search-function="freeResponseSearch"
        v-model="crewData.crewLaborers"
      />

      <bdg-text-area name="Note" v-model="crewData.note.text" />
    </template>
  </form-layout>
</template>

<script>
import BdgTextField from "../../forms/fields/BdgTextField";
import BdgMultiSelect from "../../forms/fields/BdgMultiSelect";
import LaborerCell from "../../elements/free-response-select/LaborerCell";
import { ContextMenuItem } from "../../helper-classes/ContextMenuItem";
import BdgDropdown from "../../forms/fields/BdgDropdown";
import { CrewTypes } from "../../constants/enums";
import FormLayout from "../layouts/FormLayout";
import BdgTextArea from "../../forms/fields/BdgTextArea";
import { validateRequiredRefs } from "../../helpers";

export default {
  name: "CrewForm",
  components: {
    BdgTextArea,
    FormLayout,
    BdgDropdown,
    BdgMultiSelect,
    BdgTextField
  },
  props: {
    id: Number,
    identifier: String,
    laborers: {
      type: Array,
      default: () => []
    },
    crew_supervisor_id: Number,
    crew_leader_id: Number,
    type: { type: String, default: null },
    note: {
      type: Object,
      default: () => {
        return { text: null };
      }
    }
  },
  data() {
    return {
      crewData: {
        id: this.id,
        identifier: this.identifier,
        type: this.type,
        crew_supervisor_id: this.crew_supervisor_id,
        crew_leader_id: this.crew_leader_id,
        crewLaborers: this.laborers,
        note: Object.assign({}, this.note)
      },
      crewTypes: Object.values(CrewTypes),
      selectedTypeIndex: null,
      freeResponseCellComponent: LaborerCell,
      addNewContextItem: new ContextMenuItem("Add New Crew Laborer", () => {
        console.log("Add new crew laborer...");
      }),
      requiredRefs: ["identifier", "type", "supervisor", "leader"]
    };
  },
  computed: {
    titleText() {
      return this.id ? "Edit Crew" : "Create New Crew";
    },
    allCrewSupervisors() {
      const crewSupervisors = this.$store.state.Users.crewSupervisors;
      if (crewSupervisors) {
        return crewSupervisors.map(cl => {
          return {
            label: cl.name,
            value: cl.id
          };
        });
      }
      return crewSupervisors;
    },
    allCrewLeaders() {
      const crewLeaders = this.$store.state.Users.crewLeaders;
      if (crewLeaders) {
        return crewLeaders.map(cl => {
          return {
            label: cl.name,
            value: cl.id
          };
        });
      }
      return crewLeaders;
    },
    allLaborers() {
      const laborers = this.$store.state.Contacts.crewLaborers;
      if (laborers) {
        return laborers.map(l => {
          l.label = l.name;
          return {
            ...l,
            value: l
          };
        });
      }
      return [];
    },
    availableLaborers() {
      return this.allLaborers.filter(laborer => {
        return this.crewData.crewLaborers.indexOf(laborer) === -1;
      });
    },
    isStoringCrew() {
      return this.$store.state.Crews.isStoringCrew;
    },
    isUpdatingCrew() {
      return this.$store.state.Crews.isUpdatingCrew;
    },
    isLoading() {
      return this.id ? this.isUpdatingCrew : this.isStoringCrew;
    }
  },
  mounted() {
    this.$store.dispatch("getCrewLeaders");
    this.$store.dispatch("getCrewSupervisors");
    this.$store.dispatch("getCrewLaborers");
  },
  methods: {
    goBack() {
      this.$router.replace("/crews");
    },
    selectIndex(payload) {
      this.crewData.type = this.crewTypes[payload.selectedIndex];
    },
    freeResponseSearch(search) {
      return this.availableLaborers.filter(laborer => {
        return laborer.name.toLowerCase().indexOf(search.toLowerCase()) > -1;
      });
    },
    validate() {
      return validateRequiredRefs(this, true);
    },
    saveCrew() {
      if (!this.validate()) {
        return;
      }

      if (this.id) {
        this.$store.dispatch("updateCrew", this.crewData);
      } else {
        this.$store.dispatch("storeCrew", this.crewData);
      }
    }
  }
};
</script>

<style scoped></style>
